import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useConfirm } from '@/hooks/useConfirm';
import { AppHeader } from '@/components/AppHeader';
import { RecordingStatus } from '@/components/RecordingStatus';
import { ACTIVE_VEHICLE_TYPES } from '@/config/vehicleTypes';
import {
  LOCATION_TYPE_OPTIONS,
  ROAD_CONFIGURATION_OPTIONS,
  ROAD_SURFACE_OPTIONS,
  WEATHER_OPTIONS,
  labelFor,
} from '@/config/surveyOptions';
import {
  getSurvey,
  loadCounts,
  loadIntervals,
  saveCountsSnapshot,
  updateSurvey,
} from '@/db/localDatabase';
import { isApiConfigured } from '@/services/api';
import { processQueue, queueSubmission } from '@/services/syncService';
import { downloadSurveyText } from '@/services/txtExport';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';
import { useSyncState } from '@/hooks/useSyncState';
import type { DeliveryState, IntervalCounts, Survey } from '@/types/survey';
import { totalCount } from '@/utils/counts';
import { elapsedSeconds, formatDuration } from '@/utils/duration';
import { formatClockTime, formatDisplayDate, formatNumber } from '@/utils/format';
import { formatIntervalRange } from '@/utils/intervals';
import { VehicleIcon } from '@/components/VehicleIcon';

function Row({ label, value }: { label: string; value: string | number | undefined | null }) {
  const text = value === undefined || value === null || value === '' ? '—' : String(value);
  return (
    <div className="summary-row">
      <dt className="summary-label">{label}</dt>
      <dd className="summary-value">{text}</dd>
    </div>
  );
}

const DELIVERY_LABEL: Record<DeliveryState, string> = {
  idle: 'Not sent yet',
  pending: 'Pending retry',
  success: 'Done',
  failed: 'Failed',
};

const DELIVERY_STYLE: Record<DeliveryState, string> = {
  idle: 'text-slate-500',
  pending: 'text-amber-700',
  success: 'text-emerald-700',
  failed: 'text-red-700',
};

function DeliveryRow({
  label,
  state,
  detail,
}: {
  label: string;
  state: DeliveryState;
  detail?: string;
}) {
  return (
    <div className="summary-row">
      <dt className="summary-label">{label}</dt>
      <dd className={`text-right text-base font-semibold ${DELIVERY_STYLE[state]}`}>
        {DELIVERY_LABEL[state]}
        {detail ? <span className="block text-xs font-normal text-slate-500">{detail}</span> : null}
      </dd>
    </div>
  );
}

export function SummaryPage() {
  const { surveyId = '' } = useParams();
  const navigate = useNavigate();
  const online = useNetworkStatus();
  const syncState = useSyncState();
  const { pending, confirm, answer } = useConfirm();

  const [survey, setSurvey] = useState<Survey | null>(null);
  const [intervals, setIntervals] = useState<IntervalCounts[]>([]);
  const [comments, setComments] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [localFileState, setLocalFileState] = useState<DeliveryState>('idle');
  const [localFileError, setLocalFileError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const reload = useCallback(async () => {
    const record = await getSurvey(surveyId);
    if (!record) {
      setLoading(false);
      return;
    }
    // Trust the event log over the snapshot: it is the authoritative record.
    const [counts, periods] = await Promise.all([loadCounts(surveyId), loadIntervals(surveyId)]);
    const merged = { ...record, vehicleCounts: counts };
    setSurvey(merged);
    setIntervals(periods);
    setComments(record.comments ?? '');
    setLocalFileState(record.localFileGenerated ? 'success' : 'idle');
    setLoading(false);
  }, [surveyId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // Reflect sync progress (queue runs in the background) back into this screen.
  useEffect(() => {
    if (!syncState.running) void reload();
  }, [syncState.running, syncState.lastRunAt, reload]);

  const total = useMemo(() => (survey ? totalCount(survey.vehicleCounts) : 0), [survey]);
  const duration = survey
    ? elapsedSeconds(survey.actualStartTimestamp, survey.actualEndTimestamp)
    : 0;

  // After the final submit the record becomes read-only.
  const locked = Boolean(survey?.databaseSynced && survey?.telegramSent);

  const handleCommentsBlur = async () => {
    if (!survey || locked) return;
    if ((survey.comments ?? '') === comments) return;
    await updateSurvey(survey.id, { comments });
    setSurvey({ ...survey, comments });
  };

  const handleDownload = () => {
    if (!survey) return;
    try {
      const filename = downloadSurveyText({ ...survey, comments }, intervals);
      setLocalFileState('success');
      setLocalFileError(null);
      setMessage(`Saved ${filename} to this device.`);
      void updateSurvey(survey.id, { localFileGenerated: true });
    } catch (error) {
      console.error('TXT export failed', error);
      setLocalFileState('failed');
      setLocalFileError(
        'This browser blocked the file download. The survey is still safe on the phone — try again, or use Share/Save from the browser menu.',
      );
    }
  };

  const handleSubmit = async () => {
    if (!survey || submitting) return;
    setSubmitting(true);
    setMessage(null);
    try {
      // Persist the comments and freeze the final counts before anything is sent.
      await saveCountsSnapshot(survey.id, survey.vehicleCounts);
      await updateSurvey(survey.id, {
        comments,
        status: 'pending_sync',
        submittedAt: Date.now(),
      });

      /*
       * Order matters on iOS. Triggering the .txt download can hand the page
       * over to a file viewer, which suspends JavaScript before any queued
       * request goes out — that is why iOS submissions used to sit there until
       * Retry Sync was pressed. Network delivery therefore happens first, and
       * the local file is generated once the requests are away.
       *
       * `processQueue` is also called unconditionally rather than gated on
       * `navigator.onLine`, which iOS reports unreliably in a resumed PWA.
       */
      await queueSubmission(survey.id);
      await processQueue({ force: true });

      // Local file last — independent of the network either way.
      handleDownload();

      await reload();
      setMessage(
        online
          ? 'Submitted. Check the delivery status below.'
          : 'You are offline. The survey is saved on this phone and will upload automatically when the internet returns.',
      );
    } catch (error) {
      console.error('Submit failed', error);
      setMessage(
        'Something went wrong while submitting, but nothing was lost — the survey is still stored on this phone. Use Retry Sync.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Leaves this survey behind and starts a fresh one. Anything still queued
   * keeps syncing in the background — the survey is not abandoned, just no
   * longer on screen.
   */
  const handleStartNewSurvey = async () => {
    if (survey && !survey.databaseSynced) {
      const ok = await confirm({
        title: 'Start a new survey?',
        body: `This survey has not finished uploading yet.

It stays saved on this phone and keeps retrying in the background, so nothing is lost.`,
        confirmLabel: 'Start new survey',
        cancelLabel: 'Stay here',
      });
      if (!ok) return;
    }
    navigate('/', { replace: true });
  };

  const handleRetry = async () => {
    setMessage(null);
    await processQueue({ force: true });
    await reload();
  };

  if (loading) {
    return (
      <>
        <AppHeader title="Survey Summary" />
        <div className="page-body">
          <p className="text-slate-500">Loading survey…</p>
        </div>
      </>
    );
  }

  if (!survey) {
    return (
      <>
        <AppHeader title="Survey Summary" />
        <div className="page-body space-y-4">
          <p className="text-slate-600">This survey is not stored on this device.</p>
          <button type="button" className="btn-primary w-full" onClick={() => navigate('/')}>
            Start a new survey
          </button>
        </div>
      </>
    );
  }

  const databaseState: DeliveryState = survey.databaseSynced
    ? 'success'
    : survey.submittedAt
      ? 'pending'
      : 'idle';
  const telegramState: DeliveryState = survey.telegramSent
    ? 'success'
    : survey.submittedAt
      ? 'pending'
      : 'idle';

  return (
    <>
      <AppHeader title="Survey Summary" subtitle="Step 3 of 3" />

      <div className="page-body space-y-5">
        <div className="flex items-center justify-between">
          <RecordingStatus status={survey.status} />
          <span className="font-mono text-sm text-slate-500">{survey.id}</span>
        </div>

        {message ? (
          <div
            className="rounded-xl border-2 border-brand-200 bg-brand-50 p-3 text-sm text-brand-900"
            role="status"
          >
            {message}
          </div>
        ) : null}

        <section className="card">
          <h2 className="mb-2 text-sm font-bold uppercase tracking-wider text-slate-500">Survey</h2>
          <dl>
            <Row label="Survey ID" value={survey.id} />
            <Row label="Surveyor" value={survey.surveyorName} />
            <Row label="Date" value={formatDisplayDate(survey.surveyDate)} />
            <Row label="Planned Start" value={survey.plannedStartTime} />
            <Row label="Start Time" value={formatClockTime(survey.actualStartTimestamp)} />
            <Row label="End Time" value={formatClockTime(survey.actualEndTimestamp)} />
            <Row label="Duration" value={formatDuration(duration)} />
          </dl>
        </section>

        <section className="card">
          <h2 className="mb-2 text-sm font-bold uppercase tracking-wider text-slate-500">
            Location
          </h2>
          <dl>
            <Row label="Latitude" value={survey.latitude?.toFixed(6)} />
            <Row label="Longitude" value={survey.longitude?.toFixed(6)} />
            <Row
              label="GPS Accuracy"
              value={
                survey.gpsAccuracy !== undefined ? `${Math.round(survey.gpsAccuracy)} m` : null
              }
            />
            <Row label="District" value={survey.district} />
            <Row label="Location Note" value={survey.manualLocation} />
          </dl>
        </section>

        <section className="card">
          <h2 className="mb-2 text-sm font-bold uppercase tracking-wider text-slate-500">Road</h2>
          <dl>
            <Row label="Road Number" value={survey.roadNumber} />
            <Row label="MCC No" value={survey.mccNumber} />
            <Row label="Weather" value={labelFor(WEATHER_OPTIONS, survey.weather)} />
            <Row
              label="Location Type"
              value={labelFor(LOCATION_TYPE_OPTIONS, survey.locationType)}
            />
            <Row label="Road Surface" value={labelFor(ROAD_SURFACE_OPTIONS, survey.roadSurface)} />
            <Row
              label="Road Configuration"
              value={labelFor(ROAD_CONFIGURATION_OPTIONS, survey.roadConfiguration)}
            />
            <Row label="Direction" value={survey.direction} />
            <Row label="No. of Lanes" value={survey.laneCount} />
          </dl>
        </section>

        <section className="card">
          <h2 className="mb-1 text-sm font-bold uppercase tracking-wider text-slate-500">
            15 Minute Breakdown
          </h2>
          <p className="mb-3 text-xs text-slate-500">
            Periods follow the clock (:00, :15, :30, :45), not the time counting started.
          </p>

          {intervals.length === 0 ? (
            <p className="text-sm text-slate-500">No counts were recorded.</p>
          ) : (
            /* Horizontally scrollable: 15 categories will never fit a phone. */
            <div className="-mx-1 overflow-x-auto">
              <table className="w-full min-w-[640px] border-collapse text-right text-xs">
                <thead>
                  <tr className="border-b-2 border-slate-300">
                    <th
                      scope="col"
                      className="sticky left-0 bg-white py-1.5 pr-2 text-left font-bold"
                    >
                      Period
                    </th>
                    {ACTIVE_VEHICLE_TYPES.map((type) => (
                      <th
                        key={type.id}
                        scope="col"
                        className="px-1 py-1.5 font-bold"
                        title={type.name}
                      >
                        {type.code}
                      </th>
                    ))}
                    <th scope="col" className="px-1 py-1.5 font-bold">
                      Total
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {intervals.map((interval) => (
                    <tr key={interval.start} className="border-b border-slate-100">
                      <th
                        scope="row"
                        className="sticky left-0 whitespace-nowrap bg-white py-1.5 pr-2 text-left font-medium text-slate-600"
                      >
                        {formatIntervalRange(interval.start, interval.end)}
                      </th>
                      {ACTIVE_VEHICLE_TYPES.map((type) => {
                        const value = interval.counts[type.id] ?? 0;
                        return (
                          <td
                            key={type.id}
                            className={`px-1 py-1.5 tabular-nums ${
                              value === 0 ? 'text-slate-300' : 'text-slate-900'
                            }`}
                          >
                            {value}
                          </td>
                        );
                      })}
                      <td className="px-1 py-1.5 font-bold tabular-nums">{interval.total}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-slate-900 font-bold">
                    <th scope="row" className="sticky left-0 bg-white py-1.5 pr-2 text-left">
                      TOTAL
                    </th>
                    {ACTIVE_VEHICLE_TYPES.map((type) => (
                      <td key={type.id} className="px-1 py-1.5 tabular-nums">
                        {survey.vehicleCounts[type.id] ?? 0}
                      </td>
                    ))}
                    <td className="px-1 py-1.5 tabular-nums">{total}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </section>

        <section className="card">
          <h2 className="mb-2 text-sm font-bold uppercase tracking-wider text-slate-500">
            Vehicle Counts
          </h2>
          <dl>
            {ACTIVE_VEHICLE_TYPES.map((type) => (
              <div key={type.id} className="summary-row">
                <dt className="flex min-w-0 items-center gap-2 text-sm text-slate-600">
                  <VehicleIcon
                    vehicleTypeId={type.id}
                    className="h-3.5 w-auto shrink-0 text-slate-500"
                  />
                  <span className="truncate">
                    <span className="font-semibold text-slate-800">{type.code}</span> {type.name}
                    {type.axleConfig ? (
                      <span className="text-slate-400"> · {type.axleConfig}</span>
                    ) : null}
                  </span>
                </dt>
                <dd className="summary-value tabular-nums">
                  {formatNumber(survey.vehicleCounts[type.id] ?? 0)}
                </dd>
              </div>
            ))}
          </dl>
          <div className="mt-3 flex items-baseline justify-between border-t-2 border-slate-900 pt-3">
            <span className="text-base font-bold uppercase tracking-wide">Total Vehicles</span>
            <span className="font-mono text-3xl font-bold tabular-nums">{formatNumber(total)}</span>
          </div>
        </section>

        <section className="card">
          <label htmlFor="comments" className="field-label">
            Additional Comments
          </label>
          <textarea
            id="comments"
            className="text-input min-h-[120px]"
            value={comments}
            maxLength={2000}
            disabled={locked}
            placeholder="Optional"
            onChange={(event) => setComments(event.target.value)}
            onBlur={handleCommentsBlur}
          />
          <p className="field-hint">
            Record anything important that happened during the survey, such as an accident, road
            blockage, police control, roadworks, unusual traffic conditions, weather changes, or
            other observations.
          </p>
        </section>

        <section className="card">
          <h2 className="mb-2 text-sm font-bold uppercase tracking-wider text-slate-500">
            Delivery Status
          </h2>
          <dl>
            <DeliveryRow
              label="Local File"
              state={localFileState}
              detail={localFileError ?? undefined}
            />
            <DeliveryRow
              label="Database"
              state={databaseState}
              detail={
                !isApiConfigured()
                  ? 'Server address not configured in this build.'
                  : databaseState === 'pending' && !online
                    ? 'Waiting for internet.'
                    : undefined
              }
            />
            <DeliveryRow
              label="Telegram"
              state={telegramState}
              detail={
                telegramState === 'pending' && !survey.databaseSynced
                  ? 'Sent after the database save succeeds.'
                  : undefined
              }
            />
          </dl>
          {syncState.lastError ? (
            <p className="mt-2 text-sm text-amber-800">{syncState.lastError}</p>
          ) : null}
          <p className="mt-3 text-sm text-slate-500">
            These three are independent. A failure in one never removes the survey from this phone.
          </p>
        </section>

        <div className="grid grid-cols-2 gap-2">
          <button type="button" className="btn-secondary" onClick={handleDownload}>
            Download TXT
          </button>
          <button
            type="button"
            className="btn-secondary"
            onClick={handleRetry}
            disabled={
              syncState.running || (databaseState !== 'pending' && telegramState !== 'pending')
            }
          >
            {syncState.running ? 'Syncing…' : 'Retry Sync'}
          </button>
        </div>

        <button type="button" className="btn-secondary w-full" onClick={() => navigate('/history')}>
          View History
        </button>
      </div>

      <div className="sticky-footer">
        {survey.submittedAt ? (
          /*
           * Once a survey has been submitted the next thing a surveyor almost
           * always wants is the next survey, so it gets the primary button
           * rather than being buried behind History. Re-submitting stays
           * available for the case where a delivery still needs another push.
           */
          <div className="flex gap-2">
            <button
              type="button"
              className="btn-primary flex-1 text-lg"
              onClick={handleStartNewSurvey}
            >
              Start New Survey
            </button>
            <button
              type="button"
              className="btn-secondary shrink-0 px-4"
              onClick={handleSubmit}
              disabled={submitting || locked}
            >
              {submitting ? '…' : 'Re-send'}
            </button>
          </div>
        ) : (
          <button
            type="button"
            className="btn-primary w-full text-lg"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? 'Submitting…' : 'Submit Survey'}
          </button>
        )}
        {!online ? (
          <p className="mt-2 text-center text-xs text-amber-800">
            Offline — the survey will upload automatically when the internet returns.
          </p>
        ) : null}
      </div>

      <ConfirmDialog request={pending} onAnswer={answer} />
    </>
  );
}
